import { ref, computed, watch } from '@vue/composition-api'
import useCommon from '../../useCommon'
import endpoints from '@/libs/endpoints'
import i18n from '@/libs/i18n'
import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import { generateToken, qrBaseURL, renderQRCode, uploadQRImage } from '../qrCodeImage'
import { useRouter } from '@core/utils/utils'

export default function useQRCodeAdd() {

    // BS
    const barometerSurveysCreated = ref([])
    
    const { getLocationsForDropDown, locations, getPhysicalLocationsForDropDown, physical_locations, getFeedbackPointsForDropDown, feedbackPoints, handleError } = useCommon()
    const { showSuccessMessage, showErrorMessage } = useNotifications()
    const { createItem, updateItem, getItemsAggregate, getItems  } = useLambda()
    const { physical_location_in_daily_dialogue } = JSON.parse(localStorage.getItem('clientData') || '{}')
    const { router } = useRouter()
    const qrContainer = ref(null)
    let qrInstance = null
    const qrCreated = ref(false)
    const qrData = ref({ destination: '', location: '', physical_location: '', feedbackPoint: '' })
    const qrToken = ref('')
    const isLocked = ref(false)
    const userData = store.state?.userStore?.userData
    const clientId = userData.role !== "admin" ? userData.client.$oid : null
    const collection = 'qr-code'
    const now = new Date()
    const isLoading = ref(false)
    const totalBarometerSurveysCreated = ref(0)
    const perPage = ref(5)
    const currentPage = ref(1)
    const perPageOptions = [5, 10, 15, 20, 25, 30, 40, 50, 100]
    const searchQuery = ref('')
    const sortBy = ref('name')
    const isSortDirDesc = ref(false)
    const selectedSurvey = ref({ id: '', link: '', name: '', location: '', physical_location: '' })
    const qrCodes = ref([])
    const urlAlreadyExists = ref(false)

    const qrOptions = ref([
      { title: i18n.t('label.employee-survey'), value: 'employee-survey' },
      { title: i18n.t('label.customer-survey'), value: 'customer-survey' },
      { title: i18n.t('label.barometer-survey'), value: 'barometer-survey' }
    ])

    const isFormValid = computed(() => {
        const dest = qrData.value.destination
        if (!dest) return false
        if (urlAlreadyExists.value) return false
        if (dest === 'barometer-survey') return !!selectedSurvey.value.link
        if (physical_location_in_daily_dialogue) return !!qrData.value.location && !!qrData.value.physical_location
        return !!qrData.value.location
    })

    // Table Headers Surveys Created
    const tableColumns = computed(() => ([
        { key: 'actions', label: i18n.t('message.tableHeader.actions'), thStyle: { width: '10%' } },
        { key: 'name', label: i18n.t('message.tableHeader.name'), sortable: true },
        { key: 'location', label: i18n.t('message.tableHeader.location'), sortable: true },
        { key: 'physical_location', label: i18n.t('message.tableHeader.physical_location'), sortable: true },
    ]))
    

    // URL calculation for generating the QR (without token)
    const computedBaseUrl = computed(() => {
        const dest = qrData.value.destination
        if (!dest) return ''
        if (dest === 'barometer-survey') return selectedSurvey.value.link || ''
        if (!qrData.value.location) return ''
        if (physical_location_in_daily_dialogue && !qrData.value.physical_location) return ''

        const urlBase = `${endpoints.app_url}/${dest}`
        const { location, physical_location, feedbackPoint } = qrData.value

        if (dest === 'customer-survey') {
            // physical_location_in_daily_dialogue = false
            if (!physical_location_in_daily_dialogue) {
                return `${urlBase}/${location}/${feedbackPoint.value}`
            }
            // physical_location_in_daily_dialogue = true
            if (physical_location) return `${urlBase}/${location}/${feedbackPoint.value}/${physical_location}`
            return `${urlBase}/${location}`
        }
        return physical_location
            ? `${urlBase}/${location}/${physical_location}`
            : `${urlBase}/${location}`
    })

    // Final URL encoded in the QR: base URL + rotation token
    const computedUrl = computed(() => {
        const base = computedBaseUrl.value
        if (!base || !qrToken.value) return base
        return `${qrBaseURL(base)}?t=${qrToken.value}`
    })

    // Computed filename for downloading the QR
    const fileName = computed(() => {
        const dest = qrData.value.destination

        // Helper
        const normalize = str =>
            str
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // sacar acentos
            .replace(/[^a-zA-Z0-9\-]/g, "")                  // dejar solo letras, números y guiones
            .toLowerCase()

        // barometer-survey
        if (dest === 'barometer-survey') {
            const raw = `barometer-survey-${selectedSurvey.value?.name || 'qr-code'}`
            return normalize(raw)
        }

        // Others
        const parts = []
        if (dest) parts.push(dest)
        const loc = locations.value.find(l => l.value === qrData.value.location)
        if (loc) parts.push(loc.title)
        const feedbackPoint = feedbackPoints.value.find(f => f.value === qrData.value.feedbackPoint.value)
        if (feedbackPoint) parts.push(feedbackPoint.title)
        const phy = physical_locations.value.find(p => p.value === qrData.value.physical_location)
        if (phy) parts.push(phy.title)
        const raw = parts.join("-")
        const finalName = normalize(raw)
        return finalName || "qr-code"
    })

    // Generate QR code
    const generateQR = async () => {
      if (!isFormValid.value) return
      qrContainer.value.innerHTML = ''
      if (!qrToken.value) qrToken.value = generateToken()
      qrInstance = renderQRCode(computedUrl.value, qrContainer.value)
      const canvas = qrContainer.value.querySelector('canvas')
      if (canvas) {
        canvas.style.width = '180px'
        canvas.style.height = '180px'
      }
      await createQRCode()
      qrCreated.value = true
      isLocked.value = true
    }

    // Download QR code
    const downloadQR = () => {
      if (!qrInstance) return
      const canvas = qrContainer.value.querySelector('canvas')
      const link = document.createElement('a')
      link.href = canvas.toDataURL('image/png')
      link.download = `${fileName.value}.png`
      link.click()
    }

    // Clear rendered QR
    const resetForm = () => {
        qrData.value = { destination: '', location: '', physical_location: '', feedbackPoint: '' }
        qrToken.value = ''
        qrCreated.value = false
        isLocked.value = false
        selectedSurvey.value = { id: '', link: '', name: '', location: '', physical_location: '' }
        if (qrContainer.value) qrContainer.value.innerHTML = ''
    }

    // Save the QR code with all information in the database
    const createQRCode = async () => {
        isLoading.value = true
        const dest = qrData.value.destination

        const payload = {
            client_id: oid(clientId),
            destination: dest,
            title: `${fileName.value}.png`,
            url: computedUrl.value,
            token: qrToken.value,
            created_at: date(now),
            created_by: oid(userData.worker_id.$oid),
        }

        // Source data depends on destination type
        const source = dest === 'barometer-survey' ? selectedSurvey.value : qrData.value

        if (source.location) payload.location = oid(source.location)
        if (source.physical_location) payload.physical_location = oid(source.physical_location)
        if (source.feedbackPoint) payload.feedbackPoint = oid(source.feedbackPoint.value)

        try {
            const { insertedId } = await createItem({ collection, payload })
            const key = await saveImgInAWS()
            if (insertedId && key) await updateItem({ collection, query: { _id: oid(insertedId) }, action: { $set: { imageKey: key } } })
            fetchQRCodes()
            showSuccessMessage(i18n.t('message.qr_saved'))
        } catch (error) {
            console.log(error)
            showErrorMessage(i18n.t('message.qr_error'))
        } finally {
            isLoading.value = false
        }
    }

    // Compress and save image in AWS
    const saveImgInAWS = async () => {
      const canvas = qrContainer.value.querySelector('canvas')
      return uploadQRImage(canvas, `${fileName.value}.jpeg`, clientId)
    }

    // Reset the form when the survey type changes to avoid keeping outdated data.
    watch(() => qrData.value.destination, (newVal, oldVal) => {
        if (oldVal && newVal !== oldVal) {
            resetForm()
            qrData.value.destination = newVal
        }
    })

    // Fetch existing QR codes to prevent duplicate URLs
    const fetchQRCodes = async () => {
        isLoading.value = true
        try {
            const query = {client_id: oid(clientId), deleted: { $ne: true } }
            qrCodes.value = await getItems({ collection, query }) 
        } catch (error) {
            handleError({ error, defaultMessage: i18n.t('message.qrCodes_list_fetch_error') })
        } finally {
            isLoading.value = false
        }
    }

    // Rotate the token whenever the base URL changes, so every generated QR gets a fresh token
    watch(() => computedBaseUrl.value, (newUrl) => {
        qrToken.value = newUrl ? generateToken() : ''
    })

    // Watch for duplicate URLs in existing QR codes (compare base URLs, ignoring tokens)
    watch(() => computedBaseUrl.value, (newUrl) => {
        if (!newUrl) {
            urlAlreadyExists.value = false
            return
        }
        urlAlreadyExists.value = qrCodes.value.some(qr => qrBaseURL(qr.url) === qrBaseURL(newUrl))
    })

    // BAROMETER SURVEY SECTION
    const fetchBarometerSurveysCreated = async (searchValue = '') => {
        isLoading.value = true

        try {
            const query = { client_id: oid(clientId), deleted: { $ne: true } }

            const pipeline = [
                { $match: query },
                {
                    $lookup: {
                        from: 'location',
                        localField: 'location',
                        foreignField: '_id',
                        as: 'locationData'
                    }
                },
                {
                    $lookup: {
                        from: 'physical_location',
                        localField: 'physical_location',
                        foreignField: '_id',
                        as: 'physicalLocationData'
                    }
                },
                {
                    $addFields: {
                        _id: { $toString: '$_id' },
                        location: { $arrayElemAt: ['$locationData.location', 0] },
                        physical_location: { $arrayElemAt: ['$physicalLocationData.name', 0] },
                        locationId: {  $toString: { $arrayElemAt: ['$locationData._id', 0] } },
                        physicalLocationId: {  $toString: { $arrayElemAt: ['$physicalLocationData._id', 0] } }
                    }
                },
                {
                    $project: { locationData: 0, physicalLocationData: 0 }
                }
            ]

            const items = await getItemsAggregate({ collection: 'barometer_survey_created', pipeline })
            if (!items) return
        
            // Search
            if (searchValue && searchValue !== searchQuery.value) return

            barometerSurveysCreated.value  = searchQuery.value ? items.filter(e => e.name?.toLowerCase().includes(searchQuery.value.toLowerCase())) : items
            totalBarometerSurveysCreated.value = barometerSurveysCreated.value.length
        
        } catch (error) {
            console.log(error)
            handleError({ error, defaultMessage: i18n.t('message.barometer_survey_list_fetch_error') })
        } finally {
            isLoading.value = false
        }
    }
    
    const searchBarometerSurveyCreated = searchValue => {
        isLoading.value = true
        fetchBarometerSurveysCreated(searchValue)
    }
    
    const showingMessage = computed(() => {
        const from = ((currentPage.value * perPage.value) - perPage.value) + (barometerSurveysCreated.value.length ? 1 : 0)
        const to = barometerSurveysCreated.value.length + ((currentPage.value * perPage.value) - perPage.value)
        return i18n.tc('message.paginationText', 0, { from: from, to: to, total: totalBarometerSurveysCreated.value })
    })
    
    watch([currentPage, perPage], () => {
        fetchBarometerSurveysCreated()
    })
    
    watch([searchQuery, sortBy, isSortDirDesc], () => {
        currentPage.value = 1
        fetchBarometerSurveysCreated()
    })

    const selectBarometerSurvey = (survey) => {
        if (selectedSurvey.value.id === survey._id) {
            selectedSurvey.value = { id: '', link: '', name: '', location: '', physical_location: '' }
            return
        }        
        selectedSurvey.value = {
            id: survey._id,
            link: survey.link,
            name: survey.name,
            location: survey.locationId,
            physical_location: survey.physicalLocationId || ''
        }
    }

    // Watch to update location and physical_location in qrData when feedbackPoint changes
    watch(() => qrData.value.feedbackPoint, fp => {
        if (!fp) return
        qrData.value.location = fp.location
        qrData.value.physical_location = fp.physical_location
    })


  return {
        qrContainer,
        qrCreated,
        generateQR,
        downloadQR,
        qrOptions,
        qrData,
        physical_locations,
        locations,
        feedbackPoints,
        isFormValid,
        computedUrl,
        resetForm,
        isLocked,
        isLoading,
        router,
        getLocationsForDropDown,
        getPhysicalLocationsForDropDown,
        getFeedbackPointsForDropDown,
        physical_location_in_daily_dialogue,
        urlAlreadyExists,

        // Barometer Surveys 
        fetchBarometerSurveysCreated,
        tableColumns,
        perPage,
        currentPage,
        perPageOptions,
        totalBarometerSurveysCreated,
        showingMessage,
        searchQuery,
        sortBy,
        isSortDirDesc,
        barometerSurveysCreated,
        isLoading,
        searchBarometerSurveyCreated,
        getLocationsForDropDown,
        selectedSurvey,
        selectBarometerSurvey,
        fetchQRCodes
  }
}